import moment from "moment";
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage, Conversation, nebulaChatApi } from "../../services/nebulaChat";
import { AppThunk, RootState } from "../../store";
import { messagesAdapter, messagesEntitySelectors, messageSlice, selectGlobalMessageById, selectGlobalMessageLocation, selectMessageChatById } from "../messageSlice";
import { t } from "../../../lib/polyglot.lib";
import { selectConversationById, selectExtraChatterByUuid } from "./selectors";
import { chatNSlice } from "./slice";
import { IChatIntegration, IChatMessage, InterTenantUser } from "../../../types";
import { selectCurrentUserId, userSlice } from "../userSlice";
import { selectChatSettingByKey } from "../userSlice/selectors";
import { updateActivePage } from "../appSlice";
import { selectAuthUserByUuid, selectDepartmentDictionary, selectUsersDictionary } from "../authSlice";
import { addOneChatToMessage } from "../messageSlice/thunks";
import { setActiveChunk, setScrollToMessageUuid } from "../chatSlice";
import { selectChatterByUuid } from "../multiSelectors/selectors";

export const handleConversationLeft =
    (conversationUuid: string): AppThunk =>
        (dispatch, getState) => {
            const state = getState();

            if (state.chatN.active_conversation === conversationUuid) {
                dispatch(setActiveConversation(undefined));
            }

            dispatch(chatNSlice.actions.removeConversation({ conversationUuid }))
            dispatch(messageSlice.actions.removeChat({ conversationUuid }))
        };

export const handleNewConversation =
    (initConversation: Conversation): AppThunk =>
        (dispatch) => {
            const conversation = initConversation;
            let initialChunk = messagesAdapter.getInitialState();

            if (conversation.message) {
                const latestMessage = {
                    ...conversation.message,
                    files: conversation.message?.files?.map((f, index) => ({
                        ...f,
                        content: "",
                        index,
                    })) as any
                } as any;

                initialChunk = messagesAdapter.addOne(initialChunk, latestMessage);
            }

            if (conversation.type === 'channel') {
                const time = moment().format();
                const addedMessage = {
                    content: `You've just been added to ${conversation.display_name}`,
                    time,
                    updated_at: time,
                    from: '__system__',
                    uuid: `${uuidv4()}`,
                    to: conversation.uuid
                };
                conversation.last_message_at = time;
                conversation.last_message = addedMessage.content;
                conversation.last_message_uuid = addedMessage.uuid;
                conversation.unread += 1;

                initialChunk = messagesAdapter.addOne(initialChunk, addedMessage);
            }

            dispatch(chatNSlice.actions.setOneConversation({ conversation }))
            dispatch(messageSlice.actions.addOneChatToMessage({
                chat_uuid: conversation.uuid,
                value: {
                    initialChunk,
                    searchChunk: messagesAdapter.getInitialState(),
                }
            }))
        };


export const startNewUserConversation =
    ({ message, chatUserUuid, tenantUser }:
        { message: ChatMessage, chatUserUuid?: never, tenantUser?: InterTenantUser }
        | { message?: never, chatUserUuid: string, tenantUser?: InterTenantUser }
    ): AppThunk =>
        async (dispatch, getState) => {
            const state: RootState = getState();
            const currentUser = selectCurrentUserId(state);
            // const sender = message ? selectAuthUserByUuid(state, message.from) || selectExtraChatterByUuid(state, message.from) : newChatUser

            const user = tenantUser ? undefined : selectAuthUserByUuid(state, chatUserUuid || message?.from);
            const extraChatter = message && selectExtraChatterByUuid(state, message.from);

            const unread = (message && message.from !== currentUser) ? 1 : 0;

            let conversation: Conversation | undefined;

            const userUuid = tenantUser?.user_uuid || user?.uuid || extraChatter?.uuid;

            if (userUuid) {
                conversation = {
                    uuid: userUuid,
                    type: 'user',
                    // Created by only matters for channels
                    created_by: "__user__",
                    created_at: moment.toString(),
                    unread,
                    last_message: null,
                    last_message_at: null,
                    last_message_from: null,
                    last_message_uuid: null,
                    message: null,
                    parent_message: null,
                    avatar_set: false,
                    display_name: "",
                    chat_updated_at: null,
                    is_muted: false,
                    is_favourite: false,
                    is_archived: false,
                }
            } else {
                console.error("Received message from ext, unable to resolve")
            }

            if (!conversation) return;

            let latestMessage: IChatMessage;

            if (message) {
                latestMessage = {
                    ...message,
                    files: message?.files?.map((f, index) => ({
                        ...f,
                        content: "",
                        index,
                    })) as any
                } as any;
            } else {
                latestMessage = {
                    from: 'hide',
                    content: t("phrases.this_is_start_chat_%name", {
                        name: conversation.display_name
                    }),
                    time: moment().format(),
                    updated_at: moment().format(),
                    uuid: ''
                };
            }

            const initialMessage = messagesAdapter.setOne(messagesAdapter.getInitialState(), latestMessage);

            dispatch(addOneChatToMessage({
                chat_uuid: conversation.uuid,
                value: {
                    initialChunk: initialMessage,
                    searchChunk: messagesAdapter.getInitialState(),
                }
            }))

            dispatch(chatNSlice.actions.setOneConversation({ conversation }));
        }

export const startNewIntegrationConversation =
    ({ message, integrationFromApi }: { message?: IChatMessage, integrationFromApi?: IChatIntegration }): AppThunk =>
        async (dispatch, getState) => {



            // let newIntegration;

            // if (message) {
            //     try {
            //         const { data: { result } } = await services.getIntegrationDetails(message.from);

            //         if (!result?.channel) return;

            //         newIntegration = {
            //             uuid: message?.from,
            //             display_name: result.name || result.number || t("phrases.integration_chat"),
            //             number: result.number || '',
            //             channel: getIntegrationType(result.channel)
            //         }

            //     } catch (error) {
            //         // do nothing
            //     }
            // }

            // if integration was passed as params it means that you are creating the chat
            // else use the new integration that was constructed from the message data and fetched data
            // const integration = integrationFromApi || newIntegration;

            // if for some reason there is no integration return
            // if (!integraiton) return;
            if (!integrationFromApi) return;

            const state = getState();
            const convo = selectConversationById(state, integrationFromApi.uuid);

            if (convo) {
                dispatch(setActiveConversation(convo.uuid));
                return;
            }

            // const unreadCount = message ? 1 : 0;

            const conversation: Conversation = {
                uuid: integrationFromApi.uuid,
                type: 'integration',
                created_by: '__thread__',
                created_at: moment.toString(),
                unread: 0,
                last_message: null,
                last_message_at: null,
                last_message_from: null,
                last_message_uuid: null,
                message: null,
                parent_message: null,
                integration: {
                    uuid: integrationFromApi.uuid,
                    name: integrationFromApi.display_name,
                    description: ""
                },
                avatar_set: false,
                display_name: integrationFromApi.display_name,
                chat_updated_at: null,
                is_muted: false,
                is_favourite: false,
                is_archived: false,
            };

            // const initialMessage = messagesAdapter.setOne(messagesAdapter.getInitialState(), message || {
            //     uuid: integration.uuid,
            //     from: 'hide',
            //     from_type: 'user',
            //     to: integration.uuid,
            //     to_type: 'integration',
            //     content: t("phrases.this_is_start_chat"),
            //     time: moment().format(),
            //     updated_at: moment().format()
            // })

            dispatch(addOneChatToMessage({
                chat_uuid: conversation.uuid,
                value: {
                    initialChunk: messagesAdapter.getInitialState(),
                    searchChunk: messagesAdapter.getInitialState(),
                }
            }))

            dispatch(chatNSlice.actions.setOneConversation({ conversation }));
            dispatch(setActiveConversation(conversation.uuid));
        }

export const startNewThreadConversation = (
    { threadMessage, parentMessage }:
        { threadMessage: ChatMessage, parentMessage?: never } |
        { threadMessage?: never, parentMessage: IChatMessage }
): AppThunk =>
    async (dispatch, getState) => {
        const state = getState();

        let parent_message: IChatMessage | undefined;

        if (parentMessage) {
            parent_message = parentMessage;
        } else {
            const parentMessageUuid = threadMessage.to;

            const foundParent = selectGlobalMessageById(state, parentMessageUuid);

            if (foundParent) {
                parent_message = foundParent;
            } else {
                const loadMessage = await dispatch(nebulaChatApi.endpoints.getMessageN.initiate(parentMessageUuid));

                if (loadMessage.error) {
                    console.error("Failed to load message", loadMessage.error);
                } else if (loadMessage.data) {
                    parent_message = loadMessage.data as any;
                } else {
                    console.log("Not here please sctm");
                }
            }
        }

        if (!parent_message) {
            console.error("Unable to resolve parent");
            return;
        }

        const currentUserUuid = selectCurrentUserId(state);

        const conversation: Conversation = {
            uuid: parent_message.uuid,
            type: 'thread',
            created_by: '__thread__',
            created_at: moment.toString(),
            unread: 0,
            last_message: null,
            last_message_at: null,
            last_message_from: null,
            last_message_uuid: null,
            message: null,
            parent_message: parent_message as any,
            integration: null,
            avatar_set: false,
            display_name: "",
            chat_updated_at: null,
            is_muted: false,
            is_favourite: false,
            is_archived: false,
        };

        if (threadMessage) {
            conversation.unread = threadMessage.from !== currentUserUuid ? 1 : 0;
            conversation.last_message_at = threadMessage.time;
            conversation.last_message_uuid = threadMessage.uuid;
            conversation.last_message_from = threadMessage.from;

            dispatch(addOneChatToMessage({
                chat_uuid: conversation.uuid,
                value: {
                    initialChunk: messagesAdapter.setOne(messagesAdapter.getInitialState(), {
                        ...threadMessage,
                        files: threadMessage?.files?.map((f, index) => ({
                            ...f,
                            content: "",
                            index,
                        })) as any
                    } as any),
                    searchChunk: messagesAdapter.getInitialState(),
                }
            }))
        } else {
            dispatch(addOneChatToMessage({
                chat_uuid: conversation.uuid,
                value: {
                    initialChunk: messagesAdapter.getInitialState(),
                    searchChunk: messagesAdapter.getInitialState(),
                }
            }))
        }

        dispatch(chatNSlice.actions.setOneConversation({ conversation }));
    };

export const setActiveConversation =
    (conversationUuid?: string, tenantUser?: InterTenantUser): AppThunk =>
        async (dispatch, getState) => {
            const state = getState();

            // This is for when clicking
            // If there is no conversation, we need to make one.
            if (conversationUuid) {
                const conversation = selectConversationById(state, conversationUuid);

                if (!conversation) {
                    dispatch(startNewUserConversation({ chatUserUuid: conversationUuid, tenantUser }))
                }
            }

            dispatch(chatNSlice.actions.setActiveConversation(conversationUuid));

            dispatch(userSlice.actions.updateChatSettings({
                setting: 'lastOpenChatId',
                value: conversationUuid || ""
            }));

            if (conversationUuid) {
                dispatch(updateActivePage('chat'))
            }
        };

const parseNotificationBody = (content: string, state: RootState): string => {
    const userDictionary = selectUsersDictionary(state);
    const departmentDictionary = selectDepartmentDictionary(state);

    return content
        .replace(/<UserTag uuid="(.*?)" \/>/gm, (_, uuid) =>
            `@${userDictionary[uuid]?.nickname || t("adjective.user_unknown")}`)
        .replace(/<ContactTag contact={(.*?)}\/>/gm, (_, contactJson) => {
            try {
                const contact = JSON.parse(contactJson);
                return `#${[contact.first_name, contact.last_name].filter(Boolean).join(' ') || t("adjective.user_unknown")}`;
            } catch {
                return '#unknown';
            }
        })
        .replace(/<DepartmentTag uuid="(.*?)" \/>/gm, (_, uuid) =>
            `@${departmentDictionary[uuid]?.name || 'Unknown'}`)
        .replace(/\|~(.*?)~\|/gm, (_, val) => `#${val}`);
};

const sendNebulaMessageNotification =
    (message: ChatMessage): AppThunk =>
        (dispatch, getState) => {
            if (!("Notification" in window)) return;
            if (document.hasFocus() && !document.hidden) return;
            if (Notification.permission === "denied") return;

            const state: RootState = getState();

            if (selectChatSettingByKey(state, 'disableChatNotifications')) return;

            const currentUserId = selectCurrentUserId(state);
            if (message.from === currentUserId) return;

            const conversation = selectConversationById(state, message.to);
            if (conversation?.is_muted) return;

            if (message.to_type === 'thread') {
                const parentLocation = selectGlobalMessageLocation(state, message.to);
                if (parentLocation) {
                    const parentConversation = selectConversationById(state, parentLocation.chat_uuid);
                    if (parentConversation?.is_muted) return;
                }
            }

            const chatter = selectChatterByUuid(state, message.from);
            const senderName = chatter?.name || t("phrases.new_message");
            const rawBody = message.content || (message.files?.length ? t("phrases.sent_file") : '');
            let body = message.content ? parseNotificationBody(rawBody, state) : rawBody;

            let display_name: string;
            switch (message.to_type) {
                case 'channel':
                    display_name = conversation?.display_name || t("terms.group_chat", 1);
                    body = `${senderName}: ${body}`
                    break;
                case 'thread':
                    display_name = `${t("terms.thread", 1)}: ${senderName}`;
                    break;
                default:
                    display_name = senderName;
            }

            const icon = chatter?.file_key
                ? `https://storage.googleapis.com/v-profiles/${chatter.file_key}`
                : undefined;

            const showIt = () => {
                const notification = new Notification(display_name, { body, icon });

                notification.onclick = async () => {
                    dispatch(updateActivePage('chat'));

                    if (message.to_type === 'thread') {
                        let parentChatUuid = selectGlobalMessageLocation(getState(), message.to)?.chat_uuid;

                        if (!parentChatUuid) {
                            const result = await dispatch(nebulaChatApi.endpoints.getMessageN.initiate(message.to));
                            if (result.data) {
                                parentChatUuid = result.data.to;
                            }
                        }

                        if (parentChatUuid) {
                            dispatch(focusOnConversationThreadMessage({
                                parentConversationUuid: parentChatUuid,
                                messageUuid: message.to,
                            }));
                        }
                    } else {
                        dispatch(setActiveConversation(message.to));
                    }

                    if ((window as any).electron) {
                        (window as any).electron.send('bring-window-to-front');
                    }
                    window.focus();
                    notification.close();
                };
            };

            if (Notification.permission === "granted") {
                showIt();
            } else {
                Notification.requestPermission().then((permission) => {
                    if (permission === "granted") showIt();
                });
            }
        };

export const receivedConversationMessage =
    (message: ChatMessage, handleReceived: (isRead: boolean) => void): AppThunk =>
        (dispatch, getState) => {
            const state = getState();
            // const userUuid = selectCurrentUserId(state);
            const conversation = selectConversationById(state, message.to);
            const userUuid = selectCurrentUserId(state);

            if (!conversation) {
                switch (message.to_type) {
                    case 'user':
                        dispatch(startNewUserConversation({ message }));
                        break;
                    case 'thread':
                        dispatch(startNewThreadConversation({ threadMessage: message }))
                        break;
                }
            }

            const appInView = document.hasFocus() && !document.hidden;
            const isActive = state.chatN.active_conversation === message.to && appInView;

            const unread = (
                isActive
                || message.from === userUuid
            ) ? 0 : (conversation?.unread || 0) + 1;

            if (message.from === userUuid) {
                const messageChat = selectMessageChatById(state, message.to);
                const optimistic = messageChat && messagesEntitySelectors
                    .selectAll(messageChat.initialChunk)
                    .find(m =>
                        typeof m.uuid === 'string'
                        && m.uuid.startsWith('temp-')
                        && (m.state === 'sending' || m.state === 'failed')
                        && (m.content || '') === (message.content || '')
                        && (m.reply_to || undefined) === (message.reply_to || undefined)
                    );

                if (optimistic) {
                    dispatch(messageSlice.actions.deleteOneMessage({
                        chat_uuid: message.to,
                        message_uuid: optimistic.uuid,
                    }));
                }
            }

            dispatch(chatNSlice.actions.receivedConversationMessage({
                message,
                unread,
            }));
            dispatch(messageSlice.actions.receivedConversationMessage({
                message
            }));

            if (state.chatN.active_conversation !== message.to) {
                dispatch(messageSlice.actions.pruneConversation({ chat_uuid: message.to }));
            }

            handleReceived(isActive);

            if (!isActive) {
                dispatch(sendNebulaMessageNotification(message));
            }
        };

export const focusOnConversationThreadMessage =
    ({ parentConversationUuid, messageUuid }: { parentConversationUuid: string; messageUuid: string }): AppThunk =>
        async (dispatch, getState) => {
            const state = getState();

            const conversation = selectConversationById(state, parentConversationUuid);

            if (!conversation) {
                console.error("Tried to focus on message on conversation that doesn't exist");
                return;
            }

            // Open the conversation first so the component mounts before we scroll
            await dispatch(setActiveConversation(parentConversationUuid));

            const conversationMessages = selectMessageChatById(getState(), parentConversationUuid);
            const foundInInitial = conversationMessages
                ? messagesEntitySelectors.selectById(conversationMessages.initialChunk, messageUuid)
                : undefined;

            if (foundInInitial) {
                dispatch(setActiveChunk({ chat_uuid: conversation.uuid, active_chunk: 'initial' }));
                setTimeout(() => dispatch(setScrollToMessageUuid(foundInInitial.uuid)), 250);
            } else {
                const loadMessage = await dispatch(nebulaChatApi.endpoints.getMessageN.initiate(messageUuid));

                if (loadMessage.error) {
                    console.error("Failed to load message", loadMessage.error);
                } else if (loadMessage.data) {
                    dispatch(messageSlice.actions.startSearch({ conversationUuid: parentConversationUuid, message: loadMessage.data }));
                    dispatch(setActiveChunk({ chat_uuid: conversation.uuid, active_chunk: 'search' }));
                    setTimeout(() => dispatch(setScrollToMessageUuid(loadMessage.data?.uuid || "")), 250);
                }
            }
        }
